import { API_URLS } from "@solomon-apps/config";
import { fetchJson } from "@solomon-apps/shared";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

export interface DistributionAsset {
    mint: string;
    symbol: string;
    decimals: number;
    network: "mainnet-beta" | "devnet" | "testnet";
}
export interface PublicDistribution {
    uuid: string;
    programUuid: string;
    programName: string;
    asset: DistributionAsset;
    amountUi: string;
    apyPct: string | null;
    distributedAt: string;
    transactionSignature: string;
}
export interface PublicDistributionHistory {
    distributions: PublicDistribution[];
    totals: Array<{ asset: DistributionAsset; amountUi: string }>;
    distributionCount: number;
    lastDistributionAt: string | null;
    limit: number;
    offset: number;
}
export const DISTRIBUTIONS_PAGE_SIZE = 20;

/**
 * Temporary hotfix: two test distributions from "Test USDPT Run Program"
 * (another issuer's Program, 2026-11-03) reach the public history because
 * yaas-api does not scope it to Solomon's Programs yet. Hide exactly these
 * rows and take them out of the count, totals and last date. Remove once
 * yaas-api scopes the history.
 */
const HIDDEN_DISTRIBUTIONS = [
    {
        uuid: "5cbc0619-cd65-4fc0-a2b1-34b6160aff04",
        transactionSignature:
            "4dAPu16xkiReFRAnqJiUJdd7SFa7Tc5eHq4sji6nopytKLu69TA2jbNnQD34e49LwG3RssQN5CvDhhdjNj1Dytnc",
        mint: "HVWf8JmLoHs99Lw8Psf3fyqAtA4crWxCPkrmSdNjhNH3",
        amountUi: "5",
        distributedAt: "2026-11-03T00:02:02.681Z"
    },
    {
        uuid: "b3da5288-33f9-4886-90af-4464acb663d8",
        transactionSignature:
            "4dAPu16xkiReFRAnqJiUJdd7SFa7Tc5eHq4sji6nopytKLu69TA2jbNnQD34e49LwG3RssQN5CvDhhdjNj1Dytnc",
        mint: "HVWf8JmLoHs99Lw8Psf3fyqAtA4crWxCPkrmSdNjhNH3",
        amountUi: "5",
        distributedAt: "2026-11-03T00:02:02.681Z"
    }
] as const;

function isHidden(row: Pick<PublicDistribution, "uuid" | "transactionSignature">): boolean {
    return HIDDEN_DISTRIBUTIONS.some(
        (hidden) => hidden.uuid === row.uuid && hidden.transactionSignature === row.transactionSignature
    );
}

export function hideTestDistributions(result: PublicDistributionHistory): PublicDistributionHistory {
    // Totals cover every page, so they show whether the response still carries the test rows.
    // Once yaas-api drops them, nothing here changes the response.
    const hiddenMints = new Set<string>(HIDDEN_DISTRIBUTIONS.map((hidden) => hidden.mint));
    if (!result.totals.some((total) => hiddenMints.has(total.asset.mint))) return result;
    const distributions = result.distributions.filter((row) => !isHidden(row));
    const totals = result.totals.flatMap((total) => {
        if (!hiddenMints.has(total.asset.mint)) return [total];
        const amount = HIDDEN_DISTRIBUTIONS.filter((hidden) => hidden.mint === total.asset.mint).reduce(
            (remaining, hidden) => remaining.subtract(new BigDecimal(hidden.amountUi)),
            new BigDecimal(total.amountUi)
        );
        return amount.gt(new BigDecimal("0")) ? [{ ...total, amountUi: amount.toString() }] : [];
    });
    // The test rows are the newest, so they sit on the first page with the real latest row after them.
    const hiddenDates = new Set<string>(HIDDEN_DISTRIBUTIONS.map((hidden) => hidden.distributedAt));
    const lastDistributionAt =
        result.lastDistributionAt !== null && hiddenDates.has(result.lastDistributionAt)
            ? (distributions[0]?.distributedAt ?? null)
            : result.lastDistributionAt;
    return {
        ...result,
        distributions,
        totals,
        distributionCount: Math.max(0, result.distributionCount - HIDDEN_DISTRIBUTIONS.length),
        lastDistributionAt
    };
}

export function usePublicDistributions(page: number): UseQueryResult<PublicDistributionHistory> {
    return useQuery({
        queryKey: ["public-distributions", API_URLS.YAAS, page],
        queryFn: async ({ signal }): Promise<PublicDistributionHistory> => {
            const offset = page * DISTRIBUTIONS_PAGE_SIZE;
            const result = await fetchJson<PublicDistributionHistory>(
                `${API_URLS.YAAS}/api/yaas/stats/distributions?limit=${DISTRIBUTIONS_PAGE_SIZE}&offset=${offset}`,
                signal
            );
            if (
                !result ||
                !Array.isArray(result.distributions) ||
                !Array.isArray(result.totals) ||
                !Number.isSafeInteger(result.distributionCount) ||
                result.distributionCount < 0 ||
                result.limit !== DISTRIBUTIONS_PAGE_SIZE ||
                result.offset !== offset
            ) {
                throw new Error("Invalid distribution history");
            }
            return hideTestDistributions(result);
        },
        staleTime: 60_000,
        refetchInterval: 60_000,
        retry: 2
    });
}
